import { useState } from 'react';
import { IconBell, IconFileTypePdf } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { CERTS, type CertStatus, type Certificate } from '../data/courses';
import { SearchRow } from '../courses/Courses';
import { useEdu } from '../useEdu';
import s from '../edu.module.css';

// Màn Chứng chỉ (board Mint-Training, app `cert`): bảng chứng chỉ, quản lý xem Của tôi / Toàn công ty.
// Thêm chứng chỉ (modal) và trình xem bản scan: toast giả lập, chưa ghi DB.

type Scope = 'me' | 'all';
const STATUS: Record<CertStatus, { label: string; tone: 'ok' | 'warn' | 'err' }> = {
  ok: { label: 'Còn hạn', tone: 'ok' },
  soon: { label: 'Sắp hết hạn', tone: 'warn' },
  exp: { label: 'Hết hạn', tone: 'err' },
};

function CertsBody({ list }: { list: readonly Certificate[] }) {
  const t = useT();
  const toast = useToast();
  return (
    <section className={`${s.card} ${s.tbl}`} aria-label="Chứng chỉ">
      <div className={`${s.trow} ${s.certRowGrid} ${s.thead}`}><span>{t('ed.hPerson')}</span><span>{t('ed.hCert')}</span><span>{t('ed.hSource')}</span><span>{t('ed.hExpires')}</span><span>{t('ed.hStatus')}</span><span className={s.right}>{t('ed.hScan')}</span></div>
      {list.map((c) => {
        const st = STATUS[c.status];
        return (
          <div key={`${c.owner}-${c.k}`} className={`${s.trow} ${s.certRowGrid}`}>
            <span><b className={s.strong}>{c.owner}</b> <span className={s.mute}>{c.seat}</span></span>
            <span className={s.ellip} title={`Cấp ${c.issued}`}>{c.k}</span>
            <span className={s.sub2}>{c.source}</span>
            <span>{c.expires}</span>
            <span>
              <span className={s.pill} data-tone={st.tone}>{st.label}</span>
              {c.status !== 'ok' && <span className={s.crt} data-tone={st.tone} title="Đã sinh task CRT · nhắc gia hạn" aria-label="Có task CRT nhắc gia hạn"><IconBell size={15} aria-hidden="true" /></span>}
            </span>
            <span className={s.right}>
              <button type="button" className={s.scan} aria-label={`Xem bản scan ${c.k}`} onClick={() => toast({ msg: `Mở bản scan ${c.k} trong trình xem tệp` })}><IconFileTypePdf size={16} aria-hidden="true" />PDF</button>
            </span>
          </div>
        );
      })}
      {!list.length && <div className={s.noHit}>{t('ed.noCert')}</div>}
    </section>
  );
}

export function CertsScreen() {
  const t = useT();
  const { state, learner } = useEdu();
  const phone = useLayout() === 'phone';
  const toast = useToast();
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<Scope>('me');
  const qn = q.trim().toLowerCase();
  const list = CERTS.filter((c) => (scope === 'all' && learner.manager ? true : c.owner === learner.k) && (!qn || `${c.owner}${c.k}`.toLowerCase().includes(qn)));
  return (
    <>
      <PageHeader
        title="Chứng chỉ"
        actions={state === 'ready' ? <Button variant="primary" onClick={() => toast({ msg: t('ed.noWrite') })}>{t('ed.addCert')}</Button> : undefined}
      />
      {state === 'ready' && (
        <div className={s.page}>
          {!phone && <SearchRow placeholder="Tìm người hoặc chứng chỉ" q={q} onQ={setQ} sum={`${list.length} chứng chỉ`} />}
          {learner.manager && (
            <div className={s.chips} role="group" aria-label="Phạm vi chứng chỉ">
              {([['me', 'Của tôi'], ['all', 'Toàn công ty']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={scope === k} onClick={() => setScope(k)}>{l}</button>)}
            </div>
          )}
        </div>
      )}
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có chứng chỉ nào' : undefined}
        skeleton={<div className={s.skel}><Skeleton rows={5} /></div>}
      >
        <div className={s.page}><CertsBody list={list} /></div>
      </StateView>
    </>
  );
}
